import React, { useState, useEffect } from 'react';
import { Button, Table, message, Modal, Input, Spin } from 'antd';
import { AiOutlineEdit, AiOutlineDelete } from 'react-icons/ai';
import axios from 'axios';
import { getUserID } from '../../Utility/service';
import "./AddApiKey.css";

const ManageApiKey = () => {
  const [apiKey, setApiKey] = useState(null);
  const [newApiKey, setNewApiKey] = useState('');
  const [editApiKey, setEditApiKey] = useState('');
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiKeyValid, setApiKeyValid] = useState(false);

  const getApiKey = async () => {
    try {
      const userId = getUserID();
      const response = await axios.get(`${process.env.REACT_APP_BASE_URL}api/user/get-api-key`, {
        params: { userId },
      });

      setApiKey(response.data.apiKey || null);
    } catch (error) {
      console.error('Error fetching API key:', error.response?.data?.message || error.message);
    }
  };

  const handleAddApiKey = async () => {
    if (apiKey) {
      message.error('You can only have one API Key. Please delete the existing one before adding a new one.');
      return;
    }

    if (!newApiKey.trim()) {
      message.error('API Key cannot be empty');
      return;
    }

    try {
      const userId = getUserID();
      const response = await axios.post(`${process.env.REACT_APP_BASE_URL}api/user/add-api-key`, {
        userId,
        apiKey: newApiKey,
      });

      message.success(response.data.message || 'API Key added successfully');
      setApiKey(newApiKey);
      setNewApiKey('');
    } catch (error) {
      console.error('Error adding API key:', error.response?.data?.message || error.message);
      message.error(error.response?.data?.message || 'Failed to add API key');
    }
  };

  const handleEditApiKey = async () => {
    if (!editApiKey.trim()) {
      message.error('API Key cannot be empty');
      return;
    }

    try {
      const userId = getUserID();
      const response = await axios.patch(`${process.env.REACT_APP_BASE_URL}api/user/edit-api-key`, {
        userId,
        oldApiKey: apiKey,
        newApiKey: editApiKey,
      });

      message.success(response.data.message || 'API Key updated successfully');
      setApiKey(editApiKey);
      setEditModalVisible(false);
      setEditApiKey('');
    } catch (error) {
      console.error('Error updating API key:', error.response?.data?.message || error.message);
      message.error(error.response?.data?.message || 'Failed to update API key');
    }
  };

  const handleDeleteApiKey = async () => {
    try {
      const userId = getUserID();
      const response = await axios.delete(`${process.env.REACT_APP_BASE_URL}api/user/delete-api-keys`, {
        params: { userId, apiKey },
      });

      message.success(response.data.message || 'API Key deleted successfully');
      setApiKey(null);
      window.location.reload();
    } catch (error) {
      console.error('Error deleting API key:', error.response?.data?.message || error.message);
      message.error(error.response?.data?.message || 'Failed to delete API key');
    }
  };

  const showDeleteConfirmation = () => {
    Modal.confirm({
      title: 'Are you sure you want to delete the API Key?',
      content: 'All your assistants and API keys related to the current API Key will be deleted. This action cannot be undone.',
      okText: 'Delete',
      cancelText: 'Cancel',
      onOk: handleDeleteApiKey,
      onCancel: () => {
        message.info('Deletion cancelled');
      },
    });
  };

  const verifyOpenAIKey = async (apiKey) => {
    setLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_BASE_URL}api/openai/verify-api-key`, {
        apiKey,
      });
      if (response.data.valid) {
        message.success('API Key is valid!', 3)
        setApiKeyValid(true);
      } else {
        message.error('API Key is invalid', 3);
        setApiKeyValid(false);
      }
    } catch (error) {
      console.error('Error verifying API key:', error);
      if (error.response && error.response.data && error.response.data.message) {
        message.error(error.response.data.message);
      } else {
        message.error('Error verifying API key');
      }
      setApiKeyValid(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getApiKey();
  }, []);

  return (
    <div>
      <div className="mb-4">
        {!apiKey && (
          <>
            <Input
              value={newApiKey}
              onChange={(e) => setNewApiKey(e.target.value)}
              placeholder="Enter API Key"
              className="input-field"
            />
            {apiKeyValid && (
              <Button type="primary" onClick={handleAddApiKey}>
                Add API Key
              </Button>
            )}
            <div className="input-container">
              <Spin spinning={loading} tip="Verifying..." className="spin-overlay" />
              <Button
                type="default"
                onClick={() => verifyOpenAIKey(newApiKey)}
                className="button-margin-left"
                disabled={loading}
              >
                Verify API Key
              </Button>
            </div>
          </>
        )}
        {apiKey && (
          <Table
            dataSource={[{ key: 1, apiKey }]}
            columns={[
              {
                title: 'API Key',
                dataIndex: 'apiKey',
                render: (text) => <Input value={text} readOnly />,
              },
              {
                title: 'Actions',
                key: 'actions',
                render: () => (
                  <>
                    <Button
                      shape="circle"
                      type="link"
                      onClick={() => {
                        setEditModalVisible(true);
                        setEditApiKey(apiKey);
                      }}
                    >
                      <AiOutlineEdit />
                    </Button>
                    <Button
                      shape="circle"
                      danger
                      type="link"
                      onClick={showDeleteConfirmation}
                    >
                      <AiOutlineDelete />
                    </Button>
                  </>
                ),
              },
            ]}
            pagination={false}
            bordered
          />
        )}
      </div>

      <Modal
        title="Edit API Key"
        visible={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onOk={handleEditApiKey}
        footer={[
          <Button
            key="verify"
            onClick={() => verifyOpenAIKey(editApiKey)}
            loading={loading}
          >
            Verify
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={handleEditApiKey}
            disabled={!apiKeyValid}
          >
            Submit
          </Button>,
          <Button key="cancel" onClick={() => setEditModalVisible(false)}>
            Cancel
          </Button>,
        ]}
      >
        <Input
          value={editApiKey}
          onChange={(e) => setEditApiKey(e.target.value)}
          placeholder="Enter new API Key"
        />
      </Modal>

    </div>
  );
};

export default ManageApiKey;